import yaml from 'js-yaml'
import {defineStore} from 'pinia'
import api from '@/services/api'

import {useAccessGroupsStore} from '@/stores/accessGroups'
import {useComponentsStore} from '@/stores/components'
import {useComponentStructureStore} from '@/stores/componentStructure'
import {useFieldsetsStore} from '@/stores/fieldsets'
import {useNotificationsMenuStore} from '@/stores/notificationsMenu'
import {usePageContentDataStore} from '@/stores/pageContentData'
import {useTemplatesStore} from '@/stores/templates'

export const useAppStore = defineStore('app', {
    state: () => ({
        activeFieldsets: {},
        aiMode: localStorage.getItem('aiMode') === '1',
        alphaMode: 0,
        apiEndpoint: 'https://127.0.0.1:8000',
        betaMode: 0,
        cacheNeedsClearing: false,
        ccmsEndpoint: '',
        ccmsSchemeAndHost: '',
        env: 'gae',
        isLocal: window.location.hostname.endsWith('.localhost'),
        isProduction: false,
        mainViewId: false,
        navigationComponent: '',
        pageEditorMode: false,
        settings: {},
        showSupport: false,
        sidebarToggle: {},
        sitename: window.location.host.split('.')[0].replace(/-staging$/, ''),
        websiteEndpoint: '',
    }),
    getters: {
        cacheLastCleared() {
            let time = localStorage.getItem('cacheLastCleared')
            if (time) {
                return parseInt(time)
            }
            
            let now = Math.ceil(Date.now() / 1000) + ''
            localStorage.setItem('cacheLastCleared', now)
        },
        bucketName() {
            return 'creativecms-london--' + this.databaseName
        },
        storageUrl() {
            return 'https://storage.googleapis.com/' + this.bucketName
        },
        cdnUrl() {
            return 'https://agptxipylp.cloudimg.io/v7/storage.googleapis.com/' + this.bucketName
        },
        databaseName() {
            return this.sitename?.replace(/-/g, '_')
        },
        fileSrc() {
            return (fileName) => this.storageUrl + '/assets/' + fileName
        },
        cdnSrc() {
            return (fileName) => this.cdnUrl + '/assets/' + fileName
        },
        imageWidthSrc(state) {
            return (fileName, imgWidth, modifiedDate, thumbnailWidth) => {
                let src = this.cdnSrc(fileName)
                
                if (state.sitename === 'foo-bar') {
                    src += '?v' + modifiedDate
                    
                    if (thumbnailWidth) {
                        src += '&width=' + thumbnailWidth
                    }
                    
                } else {
                    if (thumbnailWidth) {
                        src += '?width=' + thumbnailWidth
                    }
                }
                
                return src
            }
        }
    },
    actions: {
        setEndpoints() {
            // todo - Could this be simplified to !!localStorage.getItem('betaMode') ?
            let betaMode = localStorage.getItem('betaMode')
            if (betaMode) {
                this.betaMode = parseInt(betaMode)
            }
            
            let alphaMode = localStorage.getItem('alphaMode')
            if (alphaMode) {
                this.alphaMode = parseInt(alphaMode)
            }
            
            this.pageEditorMode = localStorage.getItem('pageEditorMode') === '1'
            
            // A port number exists when testing locally, so this is the flag to determine debug mode.
            if (this.isLocal) {
                // Comment out to use REMOTE endpoints when testing locally.
                this.env = 'debug'
            }
            
            switch (this.env) {
                case 'debug':
                    break
                case 'gae':
                    this.apiEndpoint = 'https://ccms-api-485199071629.europe-west2.run.app'
                    break
            }
        },
        setSidebarToggle({name, value}) {
            this.sidebarToggle[name] = value
        },
        toggleSidebar(name) {
            if (this.sidebarToggle[name] !== undefined) {
                this.sidebarToggle[name] = !this.sidebarToggle[name]
            }
        },
        async clearCache({notificationId} = {}) {
            let response
            try {
                response = await api.get('api/cache-warmer', {
                    params: {notificationId}
                })
            } catch (e) {
                // Suppresses console errors
            }
            
            this.cacheNeedsClearing = false
            
            let now = Math.ceil(Date.now() / 1000)
            localStorage.setItem('cacheLastCleared', now)
            
            await useNotificationsMenuStore().setItems()
            
            return response
        },
        async setParams() {
            this.setEndpoints()
            
            let o
            try {
                o = await api.get('api/check-build')
            } catch (err) {
                // The API will return a 404 if the targeted database does not exist.
                if (err?.response?.status === 404) {
                    window.location.href = 'https://www.creativecms.io'
                }
                throw err
            }
            
            if (o) {
                
                const localSchemeAndHost = 'https://' + this.sitename + '.wip'
                
                this.ccmsEndpoint = this.isLocal
                    ? 'http://' + this.sitename + '.ccms.localhost/'
                    : 'https://' + this.sitename + '.ccms.io/'
                
                this.websiteEndpoint = this.isLocal
                    ? localSchemeAndHost
                    : o.data.url
                
                this.ccmsSchemeAndHost = this.isLocal
                    ? localSchemeAndHost
                    : 'https://' + this.sitename + '.creativecms.io'
            }
            
            if (localStorage.getItem('token')) {
                await this.setSettings()
                await useComponentStructureStore().init()
                await useFieldsetsStore().init()
                await useComponentsStore().init()
                await useAccessGroupsStore().init()
                await usePageContentDataStore().init()
                await useTemplatesStore().init()
            }
        },
        async setSettings() {
            // Store account settings in root state.
            const response = await api.get('api/component/account_settings/1')
            const o = response?.data
            
            const clone = o ? JSON.parse(JSON.stringify(o)) : {}
            clone.config = clone.config ? yaml.load(clone.config) : {}
            
            this.settings = clone
            this.isProduction = Boolean(this.settings?.primaryDomain)
        }
    }
})